import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons (webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SOURCE_ICONS = {
  satellite: '\u{1F6F0}',
  reanalysis: '\u{1F30D}',
  'station+satellite': '\u{1F4E1}',
};

/**
 * Props:
 *   location        — { latitude, longitude, name?, elevation_m? }
 *   sources         — array of source objects with nearest_lat/lon/color/etc.
 *   onMapClick      — (lat, lng) => void — fired on every map click
 *   onSourceSelect  — (source) => void — fired when a source marker is clicked
 *   pickMode        — boolean — crosshair cursor + pick marker
 *   selectedSources — string[] — source IDs that are currently selected (highlighted)
 */
const ClimateMap = ({ location, sources, onMapClick, onSourceSelect, pickMode, selectedSources }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const coordBoxRef = useRef(null);

  // Keep callbacks in refs so marker effects don't re-fire on every render
  const onMapClickRef = useRef(onMapClick);
  const onSourceSelectRef = useRef(onSourceSelect);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);
  useEffect(() => { onSourceSelectRef.current = onSourceSelect; }, [onSourceSelect]);

  // ── Create / destroy map ──────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    // Default: South Africa country view (center ~ -29, 25, zoom 5 shows the whole country)
    const hasSpecificLocation = location?.latitude && location?.name;
    const lat = location?.latitude || -28.5;
    const lon = location?.longitude || 25.5;
    const defaultZoom = hasSpecificLocation ? 10 : 4;

    const map = L.map(containerRef.current, {
      center: [lat, lon],
      zoom: defaultZoom,
      zoomControl: true,
    });

    // === Base layers (radio — only one active at a time) ===
    const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    });
    const esri = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri, Maxar, Earthstar Geographics', maxZoom: 18 }
    );
    const topo = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri', maxZoom: 18 }
    );
    const esriStreet = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri', maxZoom: 18 }
    );

    // === Overlay layers (checkbox — multiple can be active) ===
    // Reference overlay: roads, labels, borders on top of satellite
    const esriRef = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Reference_Overlay/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 18, opacity: 0.9 }
    );
    // Transportation: roads, railways, airports
    const esriTransport = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 18, opacity: 0.8 }
    );
    // Boundaries: country, province, region borders
    const esriBoundaries = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 18, opacity: 0.9 }
    );
    // Terrain: hillshade/relief
    const esriTerrain = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 13, opacity: 0.4 }
    );
    // OpenRailwayMap
    const railway = L.tileLayer(
      'https://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png',
      { maxZoom: 18, opacity: 0.7, attribution: '&copy; OpenRailwayMap' }
    );
    // Stamen/Stadia watercolor for rivers/water (subtle)
    const waterways = L.tileLayer(
      'https://tiles.stadiamaps.com/tiles/stamen_terrain/{z}/{x}/{y}.png',
      { maxZoom: 18, opacity: 0.3, attribution: '&copy; Stadia Maps' }
    );

    osm.addTo(map);
    L.control.layers(
      // Base layers (radio buttons)
      {
        'OpenStreetMap': osm,
        'ESRI Satellite': esri,
        'ESRI Topographic': topo,
        'ESRI Street Map': esriStreet,
      },
      // Overlay layers (checkboxes)
      {
        'Roads & Labels': esriRef,
        'Transportation': esriTransport,
        'Boundaries & Places': esriBoundaries,
        'Terrain / Hillshade': esriTerrain,
        'Railways': railway,
        'Terrain Detail': waterways,
      },
      { collapsed: true }
    ).addTo(map);

    // Click → pick
    map.on('click', (e) => {
      if (onMapClickRef.current) onMapClickRef.current(e.latlng.lat, e.latlng.lng);
    });

    // GPS coordinate display on hover
    const coordDiv = L.DomUtil.create('div', 'leaflet-coord-display');
    coordDiv.style.cssText = `
      position:absolute;bottom:6px;left:50%;transform:translateX(-50%);
      z-index:1000;pointer-events:none;
      background:rgba(26,26,46,0.85);color:#4ecca3;
      font-family:'JetBrains Mono',monospace;font-size:11px;
      padding:3px 10px;border-radius:4px;
      border:1px solid rgba(78,204,163,0.3);
      backdrop-filter:blur(4px);
      white-space:nowrap;
    `;
    coordDiv.textContent = '';
    containerRef.current.appendChild(coordDiv);
    coordBoxRef.current = coordDiv;

    map.on('mousemove', (e) => {
      if (coordBoxRef.current) {
        coordBoxRef.current.textContent = `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`;
      }
    });
    map.on('mouseout', () => {
      if (coordBoxRef.current) coordBoxRef.current.textContent = '';
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      // coordDiv removed with container
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Crosshair cursor ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.cursor = pickMode ? 'crosshair' : '';
  }, [pickMode]);

  // ── Update markers ────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (!location?.latitude) return;

    // Pick-mode pulsing marker (unsaved location)
    if (pickMode && !location.name) {
      const pickIcon = L.divIcon({
        className: 'pick-marker',
        html: `<div style="
          width:36px;height:36px;border-radius:50%;
          background:rgba(78,204,163,0.3);border:3px solid #4ecca3;
          display:flex;align-items:center;justify-content:center;
          font-size:18px;box-shadow:0 0 12px rgba(78,204,163,0.5);
          animation:pulse 1.5s ease-in-out infinite;
          color:#4ecca3;font-weight:bold;
        ">+</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
      const pm = L.marker([location.latitude, location.longitude], { icon: pickIcon }).addTo(map);
      markersRef.current.push(pm);
      map.setView([location.latitude, location.longitude], map.getZoom());
      return; // no other markers in pure pick mode
    }

    // Project location marker (red pin)
    const projIcon = L.divIcon({
      className: 'project-marker',
      html: `<div style="
        width:32px;height:32px;border-radius:50%;
        background:#EF4444;border:3px solid #fff;
        display:flex;align-items:center;justify-content:center;
        font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.4);
        color:#fff;font-weight:bold;
      ">\u{1F4CD}</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
    const projMarker = L.marker([location.latitude, location.longitude], { icon: projIcon })
      .addTo(map)
      .bindPopup(`<b>${location.name || 'Project Site'}</b><br/>
        ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}<br/>
        ${location.elevation_m ? `Elevation: ${location.elevation_m}m` : ''}`);
    markersRef.current.push(projMarker);

    // Source markers
    if (sources?.length) {
      const selSet = new Set(selectedSources || []);

      sources.forEach(src => {
        if (!src.nearest_lat || !src.nearest_lon) return;
        const isSelected = selSet.has(src.id);
        const emoji = SOURCE_ICONS[src.type] || '\u{1F4CC}';
        const size = isSelected ? 34 : 28;
        const border = isSelected ? '3px solid #fff' : '2px solid #fff';
        const glow = isSelected ? `box-shadow:0 0 12px ${src.color || '#4ecca3'};` : 'box-shadow:0 2px 6px rgba(0,0,0,0.3);';

        const icon = L.divIcon({
          className: 'source-marker',
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:${src.color || '#666'};${border};
            display:flex;align-items:center;justify-content:center;
            font-size:${isSelected ? 16 : 14}px;${glow}
            cursor:pointer;transition:all 0.2s;
          ">${emoji}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });

        const availableStr = (src.variables || []).join(', ');
        // The availability API returns year_min/year_max; older payloads only carry
        // the static years_fallback string. Never render a bare `undefined`.
        const yearsStr = (src.year_min && src.year_max)
          ? `${src.year_min}–${src.year_max}`
          : (src.years_fallback || src.years || 'unknown');
        const statusLine = isSelected
          ? '<div style="color:#4ecca3;font-weight:bold;margin-top:4px">\u2713 IN USE — open Add / Download to extend it</div>'
          : '<div style="color:#F59E0B;margin-top:4px">Click this marker to configure &amp; download it</div>';

        const marker = L.marker([src.nearest_lat, src.nearest_lon], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="min-width:220px">
              <b>${src.name}</b>${isSelected ? ' \u2713' : ''}<br/>
              <small>${src.provider}</small><br/>
              <hr style="margin:4px 0;border-color:#ddd"/>
              <b>Type:</b> ${src.type}<br/>
              <b>Resolution:</b> ${src.resolution_km} km<br/>
              <b>Distance:</b> ${src.distance_km} km<br/>
              <b>Years:</b> ${yearsStr}<br/>
              <b>Variables:</b> ${availableStr}<br/>
              <b>Free:</b> ${src.free ? 'Yes' : 'No (commercial)'}<br/>
              ${src.note ? `<i style="color:#888">${src.note}</i><br/>` : ''}
              ${statusLine}
            </div>
          `);

        marker.on('click', () => {
          if (onSourceSelectRef.current) onSourceSelectRef.current(src);
        });
        markersRef.current.push(marker);

        // Distance line (thicker if selected)
        const line = L.polyline(
          [[location.latitude, location.longitude], [src.nearest_lat, src.nearest_lon]],
          {
            color: src.color || '#666',
            weight: isSelected ? 2.5 : 1,
            dashArray: isSelected ? null : '5,5',
            opacity: isSelected ? 0.8 : 0.4,
          }
        ).addTo(map);
        markersRef.current.push(line);
      });
    }

    // Fit bounds
    const markerLayers = markersRef.current.filter(m => m.getLatLng);
    if (markerLayers.length > 1) {
      const group = L.featureGroup(markerLayers);
      map.fitBounds(group.getBounds().pad(0.2));
    } else {
      map.setView([location.latitude, location.longitude], 10);
    }
  }, [location, sources, pickMode, selectedSources]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', borderRadius: 'var(--radius)', overflow: 'hidden', position: 'relative' }}
    />
  );
};

export default ClimateMap;
